import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CircleNotch, Plus, Trash, PencilSimple, X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending',        label: 'Pending',        color: 'bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]' },
  { value: 'approved',       label: 'Approved',       color: 'bg-[#F0FDF4] text-[#065F46] border-[#10B981]' },
  { value: 'live',           label: 'Live & earning', color: 'bg-[#EFF6FF] text-[#1E40AF] border-[#3B82F6]' },
  { value: 'declined_terms', label: 'Bad terms',      color: 'bg-[#FEF2F2] text-[#9A3412] border-[#FB923C]' },
  { value: 'rejected',       label: 'Rejected',       color: 'bg-[#FFF5F4] text-[#7C2D12] border-[#FF3B30]' },
];

const NETWORK_OPTIONS = [
  { value: 'awin',                label: 'AWIN' },
  { value: 'cj',                  label: 'CJ Affiliate' },
  { value: 'commission_factory',  label: 'Commission Factory (AU)' },
  { value: 'rakuten',             label: 'Rakuten' },
  { value: 'impact',              label: 'Impact' },
  { value: 'amazon_associates',   label: 'Amazon Associates' },
  { value: 'shareasale',          label: 'ShareASale' },
  { value: 'direct',              label: 'Direct / Brand Program' },
];

const REGION_OPTIONS = ['UK', 'US', 'AU', 'CA', 'GLOBAL'];

const emptyForm = {
  brand: '', network: 'awin', region: 'UK', status: 'pending',
  advertiser_id: '', commission_rate: '', cookie_days: '',
  applied_at: '', decided_at: '', reapply_after: '', notes: '', store_id: '',
};

export default function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | application_id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/applications`, { withCredentials: true });
      setApps(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing('new'); setForm({ ...emptyForm, applied_at: new Date().toISOString().slice(0, 10) }); };
  const openEdit = (app) => {
    setEditing(app.application_id);
    setForm({
      brand: app.brand || '', network: app.network || 'awin', region: app.region || 'UK',
      status: app.status || 'pending', advertiser_id: app.advertiser_id || '',
      commission_rate: app.commission_rate || '', cookie_days: app.cookie_days || '',
      applied_at: (app.applied_at || '').slice(0, 10),
      decided_at: (app.decided_at || '').slice(0, 10),
      reapply_after: (app.reapply_after || '').slice(0, 10),
      notes: app.notes || '', store_id: app.store_id || '',
    });
  };
  const closeForm = () => { setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.brand.trim()) { alert('Brand is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Cast numeric strings safely
      if (payload.cookie_days) payload.cookie_days = Number(payload.cookie_days) || payload.cookie_days;
      if (editing === 'new') {
        await axios.post(`${API}/admin/applications`, payload, { withCredentials: true });
      } else {
        await axios.patch(`${API}/admin/applications/${editing}`, payload, { withCredentials: true });
      }
      closeForm();
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this application record? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`, { withCredentials: true });
      await load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const filtered = statusFilter === 'all' ? apps : apps.filter(a => a.status === statusFilter);
  const counts = STATUS_OPTIONS.reduce((acc, s) => { acc[s.value] = apps.filter(a => a.status === s.value).length; return acc; }, {});

  if (loading) return <div className="p-8 text-center"><CircleNotch size={32} className="animate-spin inline" /></div>;

  return (
    <div className="space-y-6" data-testid="applications-tab">
      <div className="border border-[#E4E4E7] bg-white p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="font-heading font-bold text-lg mb-1">Affiliate Application Tracker</h3>
            <p className="text-sm text-[#52525B]">Keep tabs on every program you've applied to so you stop re-applying to rejected brands and stop forgetting which networks are reviewing you.</p>
          </div>
          <button
            onClick={openNew}
            className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-5 py-2.5 text-xs hover:bg-[#D62828] transition-colors flex items-center gap-2"
            data-testid="add-application-btn"
          >
            <Plus size={14} weight="bold" /> Log a new application
          </button>
        </div>

        {/* Status filter pills */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="status-filter">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide border ${statusFilter === 'all' ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}
            data-testid="filter-all"
          >
            All ({apps.length})
          </button>
          {STATUS_OPTIONS.map(s => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide border ${statusFilter === s.value ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : s.color + ' hover:opacity-80'}`}
              data-testid={`filter-${s.value}`}
            >
              {s.label} ({counts[s.value] || 0})
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-10 border-2 border-dashed border-[#E4E4E7] text-sm text-[#52525B]">
            {apps.length === 0
              ? "No applications logged yet. Click 'Log a new application' above to start tracking."
              : "No applications match this filter."}
          </div>
        )}

        {filtered.length > 0 && (
          <div className="border border-[#E4E4E7] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F4F4F5] text-[10px] uppercase tracking-wide text-[#52525B]">
                  <th className="p-3 text-left font-bold">Brand / Retailer</th>
                  <th className="p-3 text-left font-bold">Network</th>
                  <th className="p-3 text-left font-bold">Region</th>
                  <th className="p-3 text-left font-bold">Status</th>
                  <th className="p-3 text-left font-bold">Commission</th>
                  <th className="p-3 text-left font-bold">Cookie</th>
                  <th className="p-3 text-left font-bold">Applied</th>
                  <th className="p-3 text-left font-bold">Reapply after</th>
                  <th className="p-3 text-left font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const statusMeta = STATUS_OPTIONS.find(s => s.value === a.status) || STATUS_OPTIONS[0];
                  const networkMeta = NETWORK_OPTIONS.find(n => n.value === a.network);
                  return (
                    <tr key={a.application_id} className="border-t border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`app-row-${a.application_id}`}>
                      <td className="p-3 font-bold">{a.brand}</td>
                      <td className="p-3">{networkMeta?.label || a.network || '—'}</td>
                      <td className="p-3 font-mono text-xs">{a.region || '—'}</td>
                      <td className="p-3">
                        <span className={`inline-block px-2 py-1 text-[10px] font-bold uppercase border ${statusMeta.color}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="p-3">{a.commission_rate || '—'}</td>
                      <td className="p-3">{a.cookie_days ? `${a.cookie_days}d` : '—'}</td>
                      <td className="p-3 text-xs text-[#52525B]">{(a.applied_at || '').slice(0, 10) || '—'}</td>
                      <td className="p-3 text-xs text-[#52525B]">{(a.reapply_after || '').slice(0, 10) || '—'}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button onClick={() => openEdit(a)} className="text-[#0A0A0A] hover:text-[#FF3B30]" title="Edit" data-testid={`edit-${a.application_id}`}>
                            <PencilSimple size={14} weight="bold" />
                          </button>
                          <button onClick={() => remove(a.application_id)} className="text-[#52525B] hover:text-[#FF3B30]" title="Delete" data-testid={`delete-${a.application_id}`}>
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={closeForm} data-testid="app-modal">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="border-b border-[#E4E4E7] p-5 flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg">{editing === 'new' ? 'Log a new application' : 'Edit application'}</h3>
              <button onClick={closeForm} className="text-[#52525B] hover:text-[#0A0A0A]"><X size={20} weight="bold" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Brand / Retailer name *</label>
                <input type="text" value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                  placeholder="e.g. Adidas AU" data-testid="form-brand" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Network</label>
                <select value={form.network} onChange={e => setForm({ ...form, network: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm" data-testid="form-network">
                  {NETWORK_OPTIONS.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Region</label>
                <select value={form.region} onChange={e => setForm({ ...form, region: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm" data-testid="form-region">
                  {REGION_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm" data-testid="form-status">
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Advertiser ID</label>
                <input type="text" value={form.advertiser_id} onChange={e => setForm({ ...form, advertiser_id: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm font-mono"
                  placeholder="e.g. 22735" data-testid="form-advertiser-id" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Commission rate</label>
                <input type="text" value={form.commission_rate} onChange={e => setForm({ ...form, commission_rate: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                  placeholder="e.g. 3% or 8% new customers" data-testid="form-commission" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Cookie window (days)</label>
                <input type="number" value={form.cookie_days} onChange={e => setForm({ ...form, cookie_days: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                  placeholder="e.g. 30" data-testid="form-cookie-days" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Applied on</label>
                <input type="date" value={form.applied_at} onChange={e => setForm({ ...form, applied_at: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm" data-testid="form-applied-at" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Decided on</label>
                <input type="date" value={form.decided_at} onChange={e => setForm({ ...form, decided_at: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm" data-testid="form-decided-at" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Reapply after <span className="text-[10px] font-normal normal-case text-[#A1A1AA]">(rejections only)</span></label>
                <input type="date" value={form.reapply_after} onChange={e => setForm({ ...form, reapply_after: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm" data-testid="form-reapply-after" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Linked store_id</label>
                <input type="text" value={form.store_id} onChange={e => setForm({ ...form, store_id: e.target.value })}
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm font-mono"
                  placeholder="e.g. debenhams-uk" data-testid="form-store-id" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] block mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows="3"
                  className="w-full border-2 border-[#E4E4E7] bg-[#F4F4F5] px-3 py-2 focus:border-[#0A0A0A] outline-none text-sm"
                  placeholder="e.g. Rejected - site does not complement brand. Re-apply after 90 days with commission proof from other AU programs."
                  data-testid="form-notes" />
              </div>
            </div>
            <div className="border-t border-[#E4E4E7] p-5 flex gap-3 justify-end">
              <button onClick={closeForm} className="px-5 py-2.5 text-xs font-bold uppercase tracking-wide border-2 border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors" data-testid="form-cancel">
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-5 py-2.5 text-xs hover:bg-[#D62828] transition-colors disabled:opacity-50 flex items-center gap-2"
                data-testid="form-save">
                {saving ? <CircleNotch size={14} className="animate-spin" /> : null}
                {saving ? 'Saving...' : (editing === 'new' ? 'Log application' : 'Save changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
